import { Component, effect, Input, input, OnDestroy, signal } from '@angular/core';
import { RealEstate } from '../../models/real-estate.model';
import { Statuses } from '../../shared/constants/status.constants';
import { filter, Subject, takeUntil } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectAuthUser } from '../../store/selectors/auth.selectors';
import { likeRealEstate } from '../../store/actions/real-estate.actions';
declare var bootstrap: any;

@Component({
  selector: 'property-images-carousel-component',
  standalone: false,
  templateUrl: './property-images-carousel-component.component.html',
  styleUrl: './property-images-carousel-component.component.scss',
})
export class PropertyImagesCarouselComponentComponent implements OnDestroy {
  property = input<RealEstate | undefined>(undefined);
  @Input() carouselId: string = 'carouselProperty';
  @Input() imagClass: string = 'cover';
  @Input() currentImageIndex: number = 0;
  @Input() displayIndicator = false;
  @Input() displayLikeButton = true;
  @Input() listingCard = false;

  localProperty = signal<RealEstate | undefined>(undefined);

  currentImage = '';
  modalInstance: any;

  activeIndex = 0;
  visibleCount = 4;
  thumbWidth = 68; // 60px + 8px gap
  translateX = 0;

  statuses = Statuses;

  userId: number | undefined;

  private destroy$ = new Subject<void>();
  private listingId?: number;

  constructor(
    private router: Router,
    private store: Store
  ) {
    this.store
      .select(selectAuthUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userId = user?.id;
      });
    effect(() => {
      const listing = this.property();
      this.localProperty.set(listing);
      if (listing?.id !== this.listingId) {
        this.listingId = listing?.id;
        this.activeIndex = 0;
        this.currentImageIndex = 0;
        this.translateX = 0;
        this.currentImage = listing?.pictures?.[0] ?? '';
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.closeModal();
    });
  }

  closeModal() {
    const modalEl = document.getElementById('propertyImageModal');
    if (modalEl) {
      const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
      modal.hide();
    }
  }

  onSlideChanged(event: Event): void {
    this.activeIndex = (event as Event & { to: number }).to;
    this.currentImageIndex = this.activeIndex;
    this.updateSliderPosition();
  }

  openModal(index: number) {
    const modalEl = document.getElementById('propertyImageModal');
    if (!modalEl) return;

    // Reuse existing instance if it exists
    this.modalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);

    // Only show if not already shown
    if (!modalEl.classList.contains('show')) {
      this.modalInstance.show();
    }
  }

  // Needed for not jumping to the details page
  prev(event: MouseEvent) {
    const pictures = this.property()?.pictures;
    if (pictures) {
      event.stopPropagation();
      this.currentImageIndex = (this.currentImageIndex - 1 + pictures.length) % pictures.length;
      this.loadCurrent();
    }
  }

  // Needed for not jumping to the details page
  next(event: MouseEvent) {
    const pictures = this.property()?.pictures;
    if (pictures) {
      event.stopPropagation();
      this.currentImageIndex = (this.currentImageIndex + 1) % pictures.length;
      this.loadCurrent();
    }
  }

  // Needed for not jumping to the details page
  toggleLike(event: MouseEvent) {
    event.preventDefault();
    const property = this.property();
    if (property !== undefined) {
      event.stopPropagation();
      if (!this.userId) {
        this.router.navigate(['/login']);
      } else {
        if (this.isLiked()) {
          this.likeProperty(false);
        } else {
          this.likeProperty(true);
        }
      }
    }
  }

  onThumbClick(index: number) {
    this.activeIndex = index;
    this.updateSliderPosition();
  }

  updateSliderPosition() {
    const track = document.querySelector('.thumbs-track') as HTMLElement;
    const firstItem = track?.children[0] as HTMLElement;

    if (!track || !firstItem) return;

    const itemWidth = firstItem.getBoundingClientRect().width;

    const maxTranslate = track.scrollWidth - track.parentElement!.clientWidth;

    const desired = this.activeIndex * itemWidth;

    this.translateX = Math.min(desired - 60, maxTranslate);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private likeProperty(isLike: boolean) {
    const property = this.localProperty();
    if (property?.id && this.userId) {
      this.store.dispatch(
        likeRealEstate({ userId: this.userId, realEstateId: property.id, isLike: isLike })
      );
    }
  }

  isLiked(): boolean {
    return this.localProperty()?.liked === true;
  }

  isSponsered(): boolean {
    const property = this.localProperty();
    return (
      property !== undefined &&
      property?.isSponsered &&
      new Date(property.sponseredExpireDate) > new Date()
    );
  }

  private loadCurrent() {
    const pictures = this.property()?.pictures;
    if (pictures) {
      this.currentImage = pictures[this.currentImageIndex];
    }
  }
}
